import { useState, useEffect, useMemo, useCallback, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  LucideIcon,
  Package,
  Flame,
  PanelRightClose,
  PanelLeftOpen,
  ChevronDown,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import { useUseCase } from '../contexts/UseCaseContext';
import CustomerLogoBadge from './CustomerLogoBadge';
import { Button } from './ui/button';

interface Tab {
  id: string;
  label: string;
  icon: LucideIcon;
  clickable: boolean;
  indent?: boolean;
  useCase?: 'logistics' | 'emergency' | 'both';
  /**
   * Optional nested tabs (for collapsible sidebar groups).
   * Parent remains clickable; chevron toggles child visibility.
   */
  children?: Tab[];
}

interface UserInfo {
  id: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  useCases: string[];
}

interface SidebarProps {
  mainTabs: Tab[];
  bottomTabs: Tab[];
  activeTab: string;
  onChange: (tabId: string) => void;
  userInfo?: UserInfo | null;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onLogout?: () => void;
}

export default function Sidebar({ mainTabs, bottomTabs, activeTab, onChange, userInfo, collapsed = false, onToggleCollapse, onLogout }: SidebarProps) {
  const { useCase, setUseCase } = useUseCase();
  const [contentVisible, setContentVisible] = useState(!collapsed);
  /** Icon rail visibility — crossfades opposite to expanded labels. */
  const [iconsVisible, setIconsVisible] = useState(collapsed);
  const [expandedById, setExpandedById] = useState<Record<string, boolean>>({});
  const [pendingScrollToGroupId, setPendingScrollToGroupId] = useState<string | null>(null);
  /** Immediate tooltip for collapsed icon rail (avoids native title delay + clipping). */
  const [iconTooltip, setIconTooltip] = useState<{
    label: string;
    top: number;
    left: number;
  } | null>(null);
  const mainNavRef = useRef<HTMLDivElement | null>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const accentForTab = useCallback((tabId: string) => {
    // Only override accents for the Zoe tool shortcuts in the sidebar.
    // Everything else keeps the default primary accent.
    switch (tabId) {
      case 'customs-agent':
        return {
          active: 'text-brand border-transparent',
          inactive:
            'text-muted-foreground border-transparent',
          icon: (isActive: boolean) =>
            isActive
              ? 'text-brand'
              : 'text-muted-foreground group-hover:text-brand',
          label: (isActive: boolean) =>
            isActive ? '' : 'group-hover:text-brand',
        };
      case 'profit-agent':
        return {
          active: 'text-warning border-transparent',
          inactive:
            'text-muted-foreground border-transparent',
          icon: (isActive: boolean) =>
            isActive
              ? 'text-warning'
              : 'text-muted-foreground group-hover:text-warning',
          label: (isActive: boolean) =>
            isActive ? '' : 'group-hover:text-warning',
        };
      case 'investigator':
        return {
          active: 'text-warning border-transparent',
          inactive:
            'text-muted-foreground border-transparent',
          icon: (isActive: boolean) =>
            isActive
              ? 'text-warning'
              : 'text-muted-foreground group-hover:text-warning',
          label: (isActive: boolean) =>
            isActive ? '' : 'group-hover:text-warning',
        };
      default:
        return null;
    }
  }, []);

  useEffect(() => {
    if (collapsed) {
      setContentVisible(false);
      const id = setTimeout(() => setIconsVisible(true), 120);
      return () => clearTimeout(id);
    }
    setIconsVisible(false);
    setIconTooltip(null);
    const id = setTimeout(() => setContentVisible(true), 120);
    return () => clearTimeout(id);
  }, [collapsed]);

  // When tabs are provided nested, default groups to collapsed on first load.
  useEffect(() => {
    const next: Record<string, boolean> = {};
    const walk = (tabs: Tab[]) => {
      for (const t of tabs) {
        if (t.children && t.children.length > 0) {
          next[t.id] = expandedById[t.id] ?? false;
          walk(t.children);
        }
      }
    };
    walk(mainTabs);
    walk(bottomTabs);
    // Only set if we found any nested groups; avoid re-render noise.
    if (Object.keys(next).length > 0) {
      setExpandedById((prev) => ({ ...next, ...prev }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTabs, bottomTabs]);

  // Determine if user can switch use cases
  // Users can switch if they're admin or have both use cases
  const canSwitchUseCase = userInfo 
    ? (userInfo.isAdmin || (userInfo.useCases && userInfo.useCases.length > 1))
    : true; // Allow switching before authentication (will be handled by App)

  // Per-mode availability: before auth, or for admins, both modes are selectable.
  // Non-admins are limited to the use cases granted on their account.
  const canSelectLogistics = !userInfo || userInfo.isAdmin || Boolean(userInfo.useCases?.includes('logistics'));
  const canSelectEmergency = !userInfo || userInfo.isAdmin || Boolean(userInfo.useCases?.includes('emergency'));
  
  const filterTabsForUseCase = useCallback((tabs: Tab[]): Tab[] => {
    const pass = (t: Tab) => !t.useCase || t.useCase === 'both' || t.useCase === useCase;
    return tabs
      .map((t) => {
        const kids = t.children ? filterTabsForUseCase(t.children) : undefined;
        const include = pass(t) || (kids && kids.length > 0);
        return include ? { ...t, children: kids } : null;
      })
      .filter(Boolean) as Tab[];
  }, [useCase]);

  // Filter tabs based on use case (supports nested tabs)
  const filteredMainTabs = useMemo(() => filterTabsForUseCase(mainTabs), [filterTabsForUseCase, mainTabs]);
  const filteredBottomTabs = useMemo(() => filterTabsForUseCase(bottomTabs), [filterTabsForUseCase, bottomTabs]);

  const toggleExpanded = useCallback((tabId: string) => {
    setExpandedById((prev) => {
      const nextExpanded = !(prev[tabId] ?? false);
      // When expanding, schedule an auto-scroll so the child list is visible.
      if (nextExpanded) setPendingScrollToGroupId(tabId);
      return { ...prev, [tabId]: nextExpanded };
    });
  }, []);

  const groupContainsActiveTab = useCallback((tab: Tab): boolean => {
    if (tab.id === activeTab) return true;
    if (!tab.children || tab.children.length === 0) return false;
    return tab.children.some(groupContainsActiveTab);
  }, [activeTab]);

  // After a group expands, scroll the nav so all children are visible.
  useEffect(() => {
    const id = pendingScrollToGroupId;
    if (!id) return;
    if (!(expandedById[id] ?? false)) return;

    const navEl = mainNavRef.current;
    const groupEl = groupRefs.current[id];
    if (!navEl || !groupEl) return;

    // Wait for the height animation to apply.
    const raf1 = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const navRect = navEl.getBoundingClientRect();
        const groupRect = groupEl.getBoundingClientRect();

        // If the expanded group bottom is below the visible nav area, scroll down.
        const overflowBottom = groupRect.bottom - navRect.bottom;
        if (overflowBottom > 8) {
          navEl.scrollBy({ top: overflowBottom + 12, behavior: 'smooth' });
        } else {
          // If it isn't overflowing, still nudge into view (nearest).
          groupEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
        setPendingScrollToGroupId(null);
      });
    });

    return () => cancelAnimationFrame(raf1);
  }, [expandedById, pendingScrollToGroupId]);

  const renderTabButton = (tab: Tab, depth = 0) => {
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    const hasChildren = Boolean(tab.children && tab.children.length > 0);
    const isExpanded = expandedById[tab.id] ?? false;
    const indentClass = (tab.indent || depth > 0) ? 'ml-4' : '';
    const isChild = depth > 0 || Boolean(tab.indent);
    const isLeaf = !hasChildren;
    const accent = accentForTab(tab.id);

    if (!tab.clickable) {
      return (
        <div
          className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-muted-foreground"
        >
          <Icon className="w-5 h-5 text-accent transition-all duration-500" />
          <span className="text-left">{tab.label}</span>
        </div>
      );
    }

    return (
      <div
        className={`
          w-full flex items-center ${depth > 0 ? 'py-0.5' : 'py-2'} text-sm font-medium transition-all
          ${indentClass}
        `}
      >
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={() => onChange(tab.id)}
          className="flex-1 h-auto px-0 text-left justify-start items-center whitespace-normal hover:bg-transparent"
        >
          <div className={`
            group ${(isChild || isLeaf) ? 'inline-flex w-fit max-w-full' : 'flex w-full'} items-center gap-2 px-3 ${isChild ? 'py-1.5' : 'py-2'} rounded-lg transition-all border
            ${
              isActive
                ? accent?.active ?? 'text-primary border-transparent'
                : accent?.inactive ??
                  'text-muted-foreground border-transparent'
            }
          `}>
            <Icon
              className={`w-5 h-5 transition-all ${
                accent ? accent.icon(isActive) : isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-primary'
              }`}
            />
            <span className={`text-left transition-all ${accent ? accent.label(isActive) : isActive ? '' : 'group-hover:text-primary'}`}>
              {tab.label}
            </span>
          </div>
        </Button>

        {hasChildren && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              toggleExpanded(tab.id);
            }}
            className="ml-1 hover:bg-card"
            aria-label={isExpanded ? `Collapse ${tab.label}` : `Expand ${tab.label}`}
            title={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
        )}
      </div>
    );
  };

  const renderTabs = (tabs: Tab[], depth = 0) => {
    return tabs.map((tab) => {
      const hasChildren = Boolean(tab.children && tab.children.length > 0);
      const isExpanded = expandedById[tab.id] ?? false;
      return (
        <div
          key={tab.id}
          ref={(el) => {
            if (depth === 0 && hasChildren) {
              groupRefs.current[tab.id] = el;
            }
          }}
        >
          {renderTabButton(tab, depth)}
          {hasChildren && (
            <div
              className={`
                overflow-hidden pl-1
                transition-max-height-opacity duration-200 ease-out
                ${isExpanded ? 'opacity-100 max-h-130' : 'opacity-0 max-h-0'}
              `}
            >
              <div className="space-y-0.5 pt-0.5">
                {renderTabs(tab.children || [], depth + 1)}
              </div>
            </div>
          )}
        </div>
      );
    });
  };

  const showIconTooltip = (label: string, el: HTMLElement) => {
    const rect = el.getBoundingClientRect();
    setIconTooltip({
      label,
      top: rect.top + rect.height / 2,
      left: rect.right + 8,
    });
  };

  const hideIconTooltip = () => setIconTooltip(null);

  const renderCollapsedIconButton = (tab: Tab) => {
    if (!tab.clickable) return null;
    const Icon = tab.icon;
    const isActive = activeTab === tab.id;
    const accent = accentForTab(tab.id);

    return (
      <Button
        key={tab.id}
        type="button"
        variant="ghost"
        size="icon"
        onClick={() => {
          hideIconTooltip();
          onChange(tab.id);
        }}
        onMouseEnter={(e) => showIconTooltip(tab.label, e.currentTarget)}
        onMouseLeave={hideIconTooltip}
        onFocus={(e) => showIconTooltip(tab.label, e.currentTarget)}
        onBlur={hideIconTooltip}
        aria-label={tab.label}
        aria-current={isActive ? 'page' : undefined}
        className={`
          group w-9 h-9 border
          ${
            isActive
              ? accent?.active ?? 'text-primary border-transparent'
              : accent?.inactive ??
                'text-muted-foreground border-transparent'
          }
        `}
      >
        <Icon
          className={`w-5 h-5 transition-all duration-300 ${
            accent
              ? accent.icon(isActive)
              : isActive
                ? 'text-primary'
                : 'text-muted-foreground group-hover:text-primary'
          }`}
        />
      </Button>
    );
  };

  /** Flatten clickable tabs (including nested children) for the icon rail. */
  const renderCollapsedNav = (tabs: Tab[]) => {
    const nodes: ReactNode[] = [];
    const walk = (list: Tab[]) => {
      for (const tab of list) {
        const btn = renderCollapsedIconButton(tab);
        if (btn) nodes.push(btn);
        if (tab.children?.length) walk(tab.children);
      }
    };
    walk(tabs);
    return nodes;
  };

  const renderToggleButton = () =>
    onToggleCollapse ? (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onToggleCollapse}
        className="hover:text-white hover:bg-secondary"
        title={collapsed ? 'Show sidebar' : 'Hide sidebar'}
        aria-label={collapsed ? 'Show sidebar' : 'Hide sidebar'}
      >
        {collapsed ? (
          <PanelLeftOpen className="w-5 h-5" />
        ) : (
          <PanelRightClose className="w-5 h-5" />
        )}
      </Button>
    ) : null;

  const expandedOpacityClass = `transition-opacity duration-500 ease-out ${
    contentVisible && !collapsed ? 'opacity-100' : 'opacity-0'
  }`;
  const iconsOpacityClass = `transition-opacity duration-500 ease-out ${
    iconsVisible && collapsed ? 'opacity-100' : 'opacity-0'
  }`;

  return (
    <aside
      className={`h-screen bg-card border-r border-border flex flex-col transition-width duration-500 ease-out ${
        collapsed ? 'w-12' : 'w-64'
      }`}
    >
      <header className="bg-card shadow-sm border-b border-border flex-shrink-0 relative min-h-14">
        {/* Expanded header: logo + use-case toggle */}
        <div
          className={`px-4 py-4 ${expandedOpacityClass} ${collapsed ? 'pointer-events-none absolute inset-x-0 top-0' : ''}`}
          aria-hidden={collapsed || !contentVisible}
          {...((collapsed || !contentVisible) ? { inert: '' } : {})}
        >
          <div className="flex items-center justify-between gap-2 mb-4">
            <div className="min-w-0 flex-1">
              <h1 className="m-0 leading-none">
                <img
                  src="/images/landing/logo/zeefleet-v2-logo-negative-tight.png"
                  alt="ZEEFLEET"
                  width={320}
                  height={32}
                  className="block h-8 w-auto max-w-full object-left object-contain"
                  style={{ height: 32, width: 'auto', maxWidth: '100%' }}
                  loading="eager"
                  decoding="async"
                />
              </h1>
            </div>
            {renderToggleButton()}
          </div>
          {canSwitchUseCase && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold text-foreground">
                {useCase === 'emergency' ? 'Emergency' : 'Logistics'}
              </span>
              <span
                className="relative grid grid-cols-2 rounded-full border border-border bg-background p-1"
                role="group"
                aria-label="Use case"
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none absolute bottom-1 left-1 top-1 w-half-minus-4 rounded-full border border-primary/50 bg-primary/20 transition-transform duration-300 ease-out ${
                    useCase === 'emergency' ? 'translate-x-full' : 'translate-x-0'
                  }`}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-pressed={useCase === 'logistics'}
                  aria-label="Switch to Logistics"
                  title="Logistics"
                  onClick={() => {
                    if (canSelectLogistics) setUseCase('logistics');
                  }}
                  disabled={!canSelectLogistics}
                  className={`relative z-10 h-7 w-9 rounded-full ${
                    useCase === 'logistics' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Package className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-pressed={useCase === 'emergency'}
                  aria-label="Switch to Emergency"
                  title="Emergency"
                  onClick={() => {
                    if (canSelectEmergency) setUseCase('emergency');
                  }}
                  disabled={!canSelectEmergency}
                  className={`relative z-10 h-7 w-9 rounded-full ${
                    useCase === 'emergency' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Flame className="h-4 w-4" />
                </Button>
              </span>
            </div>
          )}
        </div>

        {/* Collapsed header: toggle only */}
        <div
          className={`flex items-center justify-center py-3 ${iconsOpacityClass} ${
            !collapsed ? 'pointer-events-none absolute inset-0' : ''
          }`}
          aria-hidden={!collapsed || !iconsVisible}
          {...((!collapsed || !iconsVisible) ? { inert: '' } : {})}
        >
          {renderToggleButton()}
        </div>
      </header>

      <div className="flex-1 relative min-h-0 flex flex-col">
        {/* Expanded labelled navigation */}
        <div
          className={`absolute inset-0 flex flex-col min-h-0 ${expandedOpacityClass} ${
            collapsed || !contentVisible ? 'pointer-events-none' : ''
          }`}
          aria-hidden={collapsed || !contentVisible}
          {...((collapsed || !contentVisible) ? { inert: '' } : {})}
        >
          <nav
            className="flex-1 overflow-y-auto px-4 py-3 space-y-0.5 min-h-0"
            role="navigation"
            aria-label="Main"
            ref={mainNavRef}
          >
            {renderTabs(filteredMainTabs)}
          </nav>
          <div
            className="border-t border-border px-4 py-3 space-y-0.5 flex-shrink-0"
            role="navigation"
            aria-label="Secondary"
          >
            {renderTabs(filteredBottomTabs)}
          </div>
          <div className="border-t border-border px-4 py-3 flex items-center gap-2 flex-shrink-0">
            <CustomerLogoBadge userKey={userInfo?.id} />
            <span className="flex-1 min-w-0 truncate text-sm text-muted-foreground">
              {userInfo?.email}
            </span>
            {onLogout && (
              <Button
                type="button"
                variant="ghost"
                size="iconSm"
                onClick={onLogout}
                className="hover:text-white hover:bg-card"
                aria-label="Log out"
                title="Log out"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Collapsed icon rail */}
        <div
          className={`absolute inset-0 flex flex-col min-h-0 ${iconsOpacityClass} ${
            !collapsed || !iconsVisible ? 'pointer-events-none' : ''
          }`}
          aria-hidden={!collapsed || !iconsVisible}
          {...((!collapsed || !iconsVisible) ? { inert: '' } : {})}
        >
          <nav
            className="flex-1 overflow-y-auto py-2 flex flex-col items-center gap-1 min-h-0"
            role="navigation"
            aria-label="Main"
          >
            {renderCollapsedNav(filteredMainTabs)}
          </nav>
          <div
            className="border-t border-border py-2 flex flex-col items-center gap-1 flex-shrink-0"
            role="navigation"
            aria-label="Secondary"
          >
            {renderCollapsedNav(filteredBottomTabs)}
          </div>
          <div className="border-t border-border py-2 flex flex-col items-center gap-1 flex-shrink-0">
            <div
              className="flex items-center justify-center"
              onMouseEnter={(e) => showIconTooltip(userInfo?.email ?? 'Profile picture', e.currentTarget)}
              onMouseLeave={hideIconTooltip}
            >
              <CustomerLogoBadge userKey={userInfo?.id} />
            </div>
            {onLogout && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => {
                  hideIconTooltip();
                  onLogout();
                }}
                onMouseEnter={(e) => showIconTooltip('Logout', e.currentTarget)}
                onMouseLeave={hideIconTooltip}
                onFocus={(e) => showIconTooltip('Logout', e.currentTarget)}
                onBlur={hideIconTooltip}
                aria-label="Log out"
                className="group w-9 h-9 border border-transparent hover:text-white"
              >
                <LogOut className="w-5 h-5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {iconTooltip &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-float -translate-y-1/2 whitespace-nowrap rounded-md bg-secondary px-2.5 py-1 text-xs font-medium text-foreground shadow-lg border border-border"
            style={{ top: iconTooltip.top, left: iconTooltip.left }}
          >
            {iconTooltip.label}
          </div>,
          document.body,
        )}
    </aside>
  );
}
